import React from 'react'
import { TapWorkflowEvent } from '@/types/index'
import { formatDateTime } from '@/utils/dateFormat'

interface WorkflowTimelineProps { events: TapWorkflowEvent[] }

const eventLabels: Record<string, string> = {
  StageChange: 'Stage changed',
  FlagChange: 'Flag changed',
  AssignmentChange: 'Reassigned',
  DecisionRecorded: 'Decision recorded',
  NoteAdded: 'Note added',
  FieldChange: 'Field updated',
}

// Bug #152: humanise flag values in the timeline. The server sends
// `"Urgent"` / `"OnHold"` / null; render `-` for cleared flags so the
// before/after is obvious to a non-technical reader.
const formatFlag = (v?: string | null): string => {
  if (!v) return 'None'
  if (v === 'OnHold') return 'On Hold'
  return v
}

const stageLabels: Record<string, string> = {
  Review: 'Review',
  PhoneScreen: 'Phone Screen',
  Interview: 'Interview',
  Assessment: 'Assessment',
  ReferenceCheck: 'Reference Check',
  Offer: 'Offer',
  Outcome: 'Outcome',
}

const WorkflowTimeline: React.FC<WorkflowTimelineProps> = ({ events }) => {
  if (!events.length) return <div style={{ color: 'var(--color-dark-grey)', padding: '16px' }}>No history yet.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {events.map((evt) => (
        <div key={evt.id} style={{
          display: 'flex', gap: '12px', padding: '12px', borderLeft: '3px solid var(--color-magenta)',
          backgroundColor: 'var(--color-white)', borderRadius: '0 6px 6px 0',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: '14px', color: 'var(--color-black)' }}>
              {eventLabels[evt.eventType] || evt.eventType}
              {evt.fromStage && evt.toStage && (
                <span style={{ fontWeight: 400, color: 'var(--color-dark-grey)' }}>
                  {' '}{stageLabels[evt.fromStage] || evt.fromStage} &rarr;{' '}
                  {stageLabels[evt.toStage] || evt.toStage}
                </span>
              )}
              {/* Bug #152: surface before/after for flag changes. The existing
                  UI only showed "Flag changed" with no detail. */}
              {evt.eventType === 'FlagChange' && (
                <span style={{ fontWeight: 400, color: 'var(--color-dark-grey)' }}>
                  {' '}{formatFlag(evt.fromFlag)} &rarr; {formatFlag(evt.toFlag)}
                </span>
              )}
            </div>
            {/* Bug #152: show *who* made the change so the audit log is actually useful. */}
            {evt.actorDisplayName && (
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '4px' }}>
                by <strong>{evt.actorDisplayName}</strong>
              </div>
            )}
            {evt.note && <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '4px' }}>{evt.note}</div>}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', whiteSpace: 'nowrap' }}>
            {formatDateTime(evt.occurredAt)}
          </div>
        </div>
      ))}
    </div>
  )
}

export default WorkflowTimeline
